import React, { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";

import {
  isValidEmail,
  isValidPassword,
  formatPhoneBR,
  isValidPhoneBR,
} from "@/utils";
import {
  AnalyticsMetrics,
  statesOfBrazilService,
  IBGECity,
  userService,
} from "@/services";
import {
  optionsBrazilianStates,
  optionsBodyTypes,
  optionsSkinTones,
  optionsHairColors,
  optionsEyeColors,
  optionsHeights,
  optionsSmoking,
  optionsDrinking,
  optionsMaritalStatus,
  optionsChildren,
  optionsEducation,
  optionsProfessions,
  optionsReferralSource,
  birthDate18YearsValidation,
} from "@/utils";
import { ImagePreview, SuportLink } from "@/components";
import "./RegisterStepsModal.css";

export interface RegisteStepsrModalData {
  username: string;
  email: string;
  phone: string;
  password: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  country: string;
  state: string;
  city: string;
  referralSource: string;
  bodyType: string;
  skinTone: string;
  hairColor: string;
  eyeColor: string;
  height: string;
  smoking: string;
  drinking: string;
  maritalStatus: string;
  children: string;
  education: string;
  profession: string;
  bioTitle: string;
  bioDescription: string;
  travelPreference: string;
  seekingDescription: string;
  profilePhoto?: File | null;
}

export enum RegistrationType {
  EmailPassword = "email_password",
  Google = "google",
  // Phone = "phone",
}

interface RegisterStepsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RegisteStepsrModalData) => void;
  isLoading?: boolean;
  registrationType?: RegistrationType;
}

const RegisterStepsModal: React.FC<RegisterStepsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  registrationType = RegistrationType.EmailPassword,
}) => {
  if (!isOpen) return null;

  const totalSteps = 5;

  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState<RegisteStepsrModalData>({
    username: "",
    email: "",
    phone: "",
    password: "",
    birthDay: "",
    birthMonth: "",
    birthYear: "",
    country: "Brasil",
    state: "",
    city: "",
    referralSource: "",
    bodyType: "",
    skinTone: "",
    hairColor: "",
    eyeColor: "",
    height: "",
    smoking: "",
    drinking: "",
    maritalStatus: "",
    children: "",
    education: "",
    profession: "",
    bioTitle: "",
    bioDescription: "",
    travelPreference: "",
    seekingDescription: "",
  });

  const [cities, setCities] = useState<IBGECity[]>([]);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameExistsError, setUsernameExistsError] = useState<string | null>(
    null,
  );
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [isPreviewPhotoValid, setIsPreviewPhotoValid] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [touched, setTouched] = useState<Record<string, boolean>>({
    username: false,
    phone: false,
    email: false,
    password: false,
    birthDay: false,
    birthMonth: false,
    birthYear: false,
    country: false,
    state: false,
    city: false,
    bodyType: false,
    skinTone: false,
    hairColor: false,
    eyeColor: false,
    height: false,
    smoking: false,
    drinking: false,
    maritalStatus: false,
    children: false,
    education: false,
    bioTitle: false,
    bioDescription: false,
    seekingDescription: false,
  });

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const _checkUsernameExists = async (username: string) => {
    if (!username || username.length < 3) return;

    setIsCheckingUsername(true);
    setUsernameExistsError(null);

    const exists = await userService.checkUsernameExists(username);

    if (exists) {
      setUsernameExistsError("Este nome de usuário já está em uso.");
    } else {
      setUsernameExistsError(null);
    }
    setIsCheckingUsername(false);
  };

  const handleClose = () => {
    AnalyticsMetrics.trackSignupAbandonment(`step_${step}`);
    onClose();
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleSelection = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setExpandedSection(null);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    let formattedValue = value;

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        state: value,
        city: "",
        cityId: undefined,
        region: undefined,
      }));

      return;
    }

    if (name === "city") {
      const selectedCity = cities.find((c) => c.nome === value);

      setFormData((prev) => ({
        ...prev,
        city: value,
        cityId: selectedCity?.id,
        region: selectedCity?.microrregiao.mesorregiao.UF.regiao.nome,
      }));

      return;
    }

    if (name === "phone") {
      formattedValue = formatPhoneBR(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "username") {
      _checkUsernameExists(formData.username);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const fieldsToTouch = [
        "username",
        "phone",
        "birthDate",
        "country",
        "state",
        "city",
      ];

      if (registrationType === RegistrationType.EmailPassword) {
        fieldsToTouch.push("email", "password");
      }

      setTouched((prev) => ({
        ...prev,
        ...Object.fromEntries(fieldsToTouch.map((f) => [f, true])),
      }));

      const hasStep1Errors = fieldsToTouch.some(
        (field) => errors[field as keyof typeof errors],
      );

      if (hasStep1Errors || usernameExistsError || isCheckingUsername) return;
    }

    if (step === 3) {
      flushSync(() => {
        setTouched((prev) => ({
          ...prev,
          bodyType: true,
          skinTone: true,
          hairColor: true,
          eyeColor: true,
          height: true,
        }));
      });
      const hasError =
        !formData.bodyType ||
        !formData.skinTone ||
        !formData.hairColor ||
        !formData.eyeColor ||
        !formData.height;
      if (hasError) {
        alert("Por favor, preencha todos os campos obrigatórios.");
        return;
      }
    }

    if (step === 4) {
      flushSync(() => {
        setTouched((prev) => ({
          ...prev,
          smoking: true,
          drinking: true,
          maritalStatus: true,
          children: true,
          education: true,
        }));
      });
      const hasError =
        !formData.smoking ||
        !formData.drinking ||
        !formData.maritalStatus ||
        !formData.children ||
        !formData.education;
      if (hasError) {
        alert("Por favor, preencha todos os campos obrigatórios.");
        return;
      }
    }

    if (step === 5) {
      flushSync(() => {
        setTouched((prev) => ({
          ...prev,
          bioTitle: true,
          bioDescription: true,
          seekingDescription: true,
        }));
      });
      const hasError =
        !formData.bioTitle ||
        !formData.bioDescription ||
        !formData.seekingDescription;
      if (hasError) {
        return;
      }
    }

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      onSubmit({ ...formData, profilePhoto });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const errors = useMemo(() => {
    const username = !formData.username
      ? "Informe um nome de usuário."
      : isValidEmail(formData.username)
        ? "Nome de usuário não pode ser um e-mail."
        : null;

    // Só valida E-mail se NÃO for login por Google/Celular
    const email =
      registrationType !== RegistrationType.EmailPassword
        ? null
        : !formData.email
          ? "Informe seu e-mail."
          : isValidEmail(formData.email)
            ? null
            : "E-mail inválido.";

    const phone = !formData.phone
      ? "Informe seu telefone."
      : isValidPhoneBR(formData.phone)
        ? null
        : "Telefone inválido.";

    // Só valida Senha se for fluxo tradicional por e-mail
    const password =
      registrationType !== "email_password"
        ? null
        : !formData.password
          ? "Informe uma senha."
          : isValidPassword(formData.password)
            ? null
            : "A senha deve conter maiúscula, minúscula, número e símbolo.";

    let birthDate = null;

    if (!formData.birthDay || !formData.birthMonth || !formData.birthYear) {
      birthDate = "Informe a data completa.";
    } else {
      const year = formData.birthYear;
      const month = String(formData.birthMonth).padStart(2, "0");
      const day = String(formData.birthDay).padStart(2, "0");
      const formattedDateStr = `${year}-${month}-${day}`;

      // Executa a validação externa
      const isAdult = birthDate18YearsValidation(formattedDateStr);

      if (!isAdult) {
        birthDate = "Você precisa ter mais de 18 anos para se cadastrar.";
      }
    }

    const country = !formData.country ? "Selecione o país." : null;
    const state = !formData.state ? "Selecione o estado." : null;
    const city = !formData.city ? "Selecione a cidade." : null;

    const bodyType = !formData.bodyType ? "Selecione o tipo de corpo." : null;
    const skinTone = !formData.skinTone ? "Selecione o tom de pele." : null;
    const hairColor = !formData.hairColor ? "Selecione a cor do cabelo." : null;
    const eyeColor = !formData.eyeColor ? "Selecione a cor dos olhos." : null;
    const height = !formData.height ? "Selecione a altura." : null;
    const smoking = !formData.smoking ? "Selecione se fuma." : null;
    const drinking = !formData.drinking ? "Selecione se bebe." : null;
    const maritalStatus = !formData.maritalStatus
      ? "Selecione o estado civil."
      : null;
    const children = !formData.children ? "Selecione se tem filhos." : null;
    const education = !formData.education ? "Selecione a escolaridade." : null;
    const bioTitle = !formData.bioTitle ? "Escreva uma frase de efeito." : null;
    const bioDescription = !formData.bioDescription
      ? "Escreva uma descrição."
      : null;
    const seekingDescription = !formData.seekingDescription
      ? "Descreva o que busca."
      : null;

    return {
      username,
      email,
      phone,
      password,
      birthDate,
      country,
      state,
      city,
      bodyType,
      skinTone,
      hairColor,
      eyeColor,
      height,
      smoking,
      drinking,
      maritalStatus,
      children,
      education,
      bioTitle,
      bioDescription,
      seekingDescription,
    };
  }, [formData, registrationType]);

  useEffect(() => {
    const loadCities = async () => {
      if (!formData.state) {
        setCities([]);
        return;
      }

      try {
        const result = await statesOfBrazilService.getCitiesByState(
          formData.state,
        );
        setCities(result);
      } catch (error) {
        console.error("Erro ao carregar cidades:", error);
        setCities([]);
      }
    };

    loadCities();
  }, [formData.state]);

  return (
    <div className="modal-overlay">
      <div className="modal-content-register">
        <button className="modal-close" onClick={handleClose}>
          &times;
        </button>

        <div className="step-indicator">
          {step} de {totalSteps}
        </div>
        <h2 className="modal-title">
          {step === 1 && "Complete seu cadastro"}
          {step === 2 && "Foto de perfil"}
          {step === 3 && "Sobre você"}
          {step === 4 && "Estamos quase lá"}
          {step === 5 && "Últimos detalhes"}
        </h2>

        {step === 1 && (
          <div className="step-content">
            <div className="registration-notice-banner">
              <div className="notice-icon">⚠️</div>
              <div className="notice-text">
                <strong>Atenção:</strong> Perfis <strong>sem foto</strong>{" "}
                perdem até 85% de visibilidade e podem ser arquivados. Prepare
                uma foto nítida para a próxima etapa!
              </div>
            </div>

            <div className="form-group">
              <label>Nome de usuário</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.username && errors.username ? "error" : ""}
                  placeholder="Apelidos mantêm sua privacidade."
                />
                {isCheckingUsername && (
                  <span className="checking-text">Verificando...</span>
                )}

                {touched.username &&
                  (usernameExistsError || errors.username) && (
                    <span className="error-message">
                      {usernameExistsError || errors.username}
                    </span>
                  )}
              </div>
            </div>

            {registrationType === RegistrationType.EmailPassword && (
              <div className="form-group">
                <label>E-mail</label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={touched.email && errors.email ? "error" : ""}
                  placeholder="Ex.: nome@email.com.br"
                />
                {touched.email && errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Celular</label>
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.phone && errors.phone ? "error" : ""}
                placeholder="(00) 00000-0000"
                maxLength={15}
              />
              {touched.phone && errors.phone && (
                <span className="error-message">{errors.phone}</span>
              )}
            </div>

            {registrationType === RegistrationType.EmailPassword && (
              <div className="form-group">
                <label>Senha</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      touched.password && errors.password ? "error" : ""
                    }
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Data de nascimento</label>
              <div className="date-group">
                <select
                  name="birthDay"
                  value={formData.birthDay}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    (touched.birthDay ||
                      touched.birthMonth ||
                      touched.birthYear) &&
                    errors.birthDate
                      ? "error"
                      : ""
                  }
                >
                  <option value="">Dia</option>
                  {days.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                <select
                  name="birthMonth"
                  value={formData.birthMonth}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    (touched.birthDay ||
                      touched.birthMonth ||
                      touched.birthYear) &&
                    errors.birthDate
                      ? "error"
                      : ""
                  }
                >
                  <option value="">Mês</option>
                  {months.map((m, index) => (
                    <option key={index} value={index + 1}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  name="birthYear"
                  value={formData.birthYear}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    (touched.birthDay ||
                      touched.birthMonth ||
                      touched.birthYear) &&
                    errors.birthDate
                      ? "error"
                      : ""
                  }
                >
                  <option value="">Ano</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              {(touched.birthDay || touched.birthMonth || touched.birthYear) &&
                errors.birthDate && (
                  <span className="error-message">{errors.birthDate}</span>
                )}
            </div>

            <div className="form-group">
              <label>País</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.country && errors.country ? "error" : ""}
              >
                <option value="Brasil">Brasil</option>
              </select>
              {touched.country && errors.country && (
                <span className="error-message">{errors.country}</span>
              )}
            </div>

            <div className="form-group">
              <label>Estado</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.state && errors.state ? "error" : ""}
              >
                <option value="">Selecione uma opção</option>
                {optionsBrazilianStates.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {touched.state && errors.state && (
                <span className="error-message">{errors.state}</span>
              )}
            </div>

            <div className="form-group">
              <label>Cidade</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.city && errors.city ? "error" : ""}
                disabled={!formData.state}
              >
                <option value="">
                  {formData.state
                    ? "Selecione uma opção"
                    : "Selecione o estado primeiro"}
                </option>

                {cities.map((city) => (
                  <option key={city.id} value={city.nome}>
                    {city.nome}
                  </option>
                ))}
              </select>
              {touched.city && errors.city && (
                <span className="error-message">{errors.city}</span>
              )}
            </div>

            <div className="form-group">
              <label>Como ouviu falar sobre nós? (opcional)</label>
              <select
                name="referralSource"
                value={formData.referralSource}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="">Selecione uma opção</option>
                {optionsReferralSource.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button className="steps-btn-primary" onClick={handleNext}>
                Continuar
              </button>
            </div>

            <SuportLink />
          </div>
        )}

        {step === 2 && (
          <div className="step-content">
            <div className="registration-notice-banner">
              <div className="notice-icon">⚠️</div>
              <div className="notice-text">
                <strong>Atenção:</strong> Perfis <strong>sem foto</strong>{" "}
                perdem até 85% de visibilidade e podem ser arquivados. Prepare
                uma foto nítida!
              </div>
            </div>

            <ImagePreview
              value={profilePhoto}
              onChange={(file) => setProfilePhoto(file)}
              isValid={(isValid) => {
                setIsPreviewPhotoValid(isValid);
              }}
            />

            <div className="modal-actions">
              <button className="steps-btn-secondary" onClick={handleBack}>
                Voltar
              </button>

              <button
                className="steps-btn-primary"
                onClick={handleNext}
                disabled={!profilePhoto || !isPreviewPhotoValid}
              >
                Continuar
              </button>
            </div>

            <SuportLink />
          </div>
        )}

        {step === 3 && (
          <div className="step-content">
            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "bodyType" ? "active" : ""}`}
                onClick={() => toggleSection("bodyType")}
              >
                <div className="register-accordion-title-group">
                  <span>Tipo de corpo</span>
                  {formData.bodyType && (
                    <span className="selected-value">{formData.bodyType}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "bodyType" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "bodyType" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsBodyTypes.map((type) => (
                    <div
                      key={type}
                      className={`option-item ${formData.bodyType === type ? "selected" : ""}`}
                      onClick={() => handleSelection("bodyType", type)}
                    >
                      {type}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Skin Tone */}
            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "skinTone" ? "active" : ""}`}
                onClick={() => toggleSection("skinTone")}
              >
                <div className="register-accordion-title-group">
                  <span>Tom de Pele</span>
                  {formData.skinTone && (
                    <span className="selected-value">{formData.skinTone}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "skinTone" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "skinTone" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsSkinTones.map((tone) => (
                    <div
                      key={tone}
                      className={`option-item ${formData.skinTone === tone ? "selected" : ""}`}
                      onClick={() => handleSelection("skinTone", tone)}
                    >
                      {tone}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "hairColor" ? "active" : ""}`}
                onClick={() => toggleSection("hairColor")}
              >
                <div className="register-accordion-title-group">
                  <span>Cabelo</span>
                  {formData.hairColor && (
                    <span className="selected-value">{formData.hairColor}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "hairColor" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "hairColor" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsHairColors.map((color) => (
                    <div
                      key={color}
                      className={`option-item ${formData.hairColor === color ? "selected" : ""}`}
                      onClick={() => handleSelection("hairColor", color)}
                    >
                      {color}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "eyeColor" ? "active" : ""}`}
                onClick={() => toggleSection("eyeColor")}
              >
                <div className="register-accordion-title-group">
                  <span>Cor dos olhos</span>
                  {formData.eyeColor && (
                    <span className="selected-value">{formData.eyeColor}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "eyeColor" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "eyeColor" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsEyeColors.map((color) => (
                    <div
                      key={color}
                      className={`option-item ${formData.eyeColor === color ? "selected" : ""}`}
                      onClick={() => handleSelection("eyeColor", color)}
                    >
                      {color}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "height" ? "active" : ""}`}
                onClick={() => toggleSection("height")}
              >
                <div className="register-accordion-title-group">
                  <span>Sua altura</span>
                  {formData.height && (
                    <span className="selected-value">{formData.height} cm</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "height" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "height" ? "open" : ""}`}
              >
                <div className="height-select-container">
                  <select
                    name="height"
                    value={formData.height}
                    onChange={(e) => handleChange(e)}
                    className="full-width-select"
                  >
                    <option value="">Selecione uma opção</option>
                    {optionsHeights.map((h) => (
                      <option key={h} value={h}>
                        {h} cm
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="steps-btn-secondary" onClick={handleBack}>
                Voltar
              </button>
              <button className="steps-btn-primary" onClick={handleNext}>
                Continuar
              </button>
            </div>

            <SuportLink />
          </div>
        )}

        {step === 4 && (
          <div className="step-content">
            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "smoking" ? "active" : ""}`}
                onClick={() => toggleSection("smoking")}
              >
                <div className="register-accordion-title-group">
                  <span>Você fuma?</span>
                  {formData.smoking && (
                    <span className="selected-value">{formData.smoking}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "smoking" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "smoking" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsSmoking.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.smoking === option ? "selected" : ""}`}
                      onClick={() => handleSelection("smoking", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "drinking" ? "active" : ""}`}
                onClick={() => toggleSection("drinking")}
              >
                <div className="register-accordion-title-group">
                  <span>Você bebe?</span>
                  {formData.drinking && (
                    <span className="selected-value">{formData.drinking}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "drinking" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "drinking" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsDrinking.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.drinking === option ? "selected" : ""}`}
                      onClick={() => handleSelection("drinking", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "maritalStatus" ? "active" : ""}`}
                onClick={() => toggleSection("maritalStatus")}
              >
                <div className="register-accordion-title-group">
                  <span>Estado civil</span>
                  {formData.maritalStatus && (
                    <span className="selected-value">
                      {formData.maritalStatus}
                    </span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "maritalStatus" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "maritalStatus" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsMaritalStatus.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.maritalStatus === option ? "selected" : ""}`}
                      onClick={() => handleSelection("maritalStatus", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tem filhos? */}
            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "children" ? "active" : ""}`}
                onClick={() => toggleSection("children")}
              >
                <div className="register-accordion-title-group">
                  <span>Tem filhos?</span>
                  {formData.children && (
                    <span className="selected-value">{formData.children}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "children" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "children" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsChildren.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.children === option ? "selected" : ""}`}
                      onClick={() => handleSelection("children", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "education" ? "active" : ""}`}
                onClick={() => toggleSection("education")}
              >
                <div className="register-accordion-title-group">
                  <span>Escolaridade</span>
                  {formData.education && (
                    <span className="selected-value">{formData.education}</span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "education" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "education" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsEducation.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.education === option ? "selected" : ""}`}
                      onClick={() => handleSelection("education", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="register-accordion-item">
              <button
                className={`register-accordion-header ${expandedSection === "profession" ? "active" : ""}`}
                onClick={() => toggleSection("profession")}
              >
                <div className="register-accordion-title-group">
                  <span>Profissão (opcional)</span>
                  {formData.profession && (
                    <span className="selected-value">
                      {formData.profession}
                    </span>
                  )}
                </div>
                <span className="register-accordion-icon">
                  {expandedSection === "profession" ? "▲" : "▼"}
                </span>
              </button>
              <div
                className={`register-accordion-body ${expandedSection === "profession" ? "open" : ""}`}
              >
                <div className="options-list">
                  {optionsProfessions.map((option) => (
                    <div
                      key={option}
                      className={`option-item ${formData.profession === option ? "selected" : ""}`}
                      onClick={() => handleSelection("profession", option)}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button className="steps-btn-secondary" onClick={handleBack}>
                Voltar
              </button>
              <button className="steps-btn-primary" onClick={handleNext}>
                Continuar
              </button>
            </div>

            <SuportLink />
          </div>
        )}

        {step === 5 && (
          <div className="step-content">
            <div
              className="form-group bio-title-group"
              style={{ position: "relative" }}
            >
              <label>Frase de apresentação</label>
              <input
                type="text"
                name="bioTitle"
                value={formData.bioTitle || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                className={touched.bioTitle && errors.bioTitle ? "error" : ""}
                maxLength={128}
                placeholder="Faça valer a primeira impressão"
              />
              {touched.bioTitle && errors.bioTitle && (
                <span className="error-message">{errors.bioTitle}</span>
              )}
              <div
                className="char-counter"
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#999",
                  marginTop: "4px",
                }}
              >
                {formData.bioTitle?.length || 0}/128
              </div>
            </div>

            <div
              className="form-group bio-desc-group"
              style={{ position: "relative" }}
            >
              <label>Uma breve descrição sobre você</label>
              <textarea
                name="bioDescription"
                value={formData.bioDescription || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({ ...prev, bioDescription: val }));
                }}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, bioDescription: true }))
                }
                maxLength={3000}
                placeholder="Apresente-se de forma simples e objetiva"
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "4px",
                  border:
                    touched.bioDescription && errors.bioDescription
                      ? "1px solid #ff0000"
                      : "1px solid #ddd",
                  minHeight: "100px",
                  resize: "vertical",
                }}
              />
              {touched.bioDescription && errors.bioDescription && (
                <span className="error-message">{errors.bioDescription}</span>
              )}
              <div
                className="char-counter"
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#999",
                  marginTop: "4px",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "10px",
                  marginBottom: "10px",
                }}
              >
                {formData.bioDescription?.length || 0}/3000
              </div>
            </div>

            <div className="form-group">
              <label>Gosta de viajar (opcional)</label>
              <div
                className="register-accordion-item"
                style={{ border: "none", borderBottom: "1px solid #eee" }}
              >
                <button
                  className={`register-accordion-header ${expandedSection === "travelPreference" ? "active" : ""}`}
                  onClick={() => toggleSection("travelPreference")}
                  style={{
                    padding: "10px 0",
                    border: "none",
                    background: "none",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="register-accordion-title-group">
                    <span style={{ fontWeight: "normal", color: "#555" }}>
                      {formData.travelPreference
                        ? formData.travelPreference
                        : "Selecione uma opção"}
                    </span>
                  </div>
                  <span className="register-accordion-icon">
                    {expandedSection === "travelPreference" ? "▲" : "▼"}
                  </span>
                </button>
                <div
                  className={`register-accordion-body ${expandedSection === "travelPreference" ? "open" : ""}`}
                >
                  <div className="options-list">
                    {[
                      "Mundo inteiro",
                      "Somente no meu país",
                      "Somente no meu estado",
                      "Somente na minha cidade",
                    ].map((option) => (
                      <div
                        key={option}
                        className={`option-item ${formData.travelPreference === option ? "selected" : ""}`}
                        onClick={() =>
                          handleSelection("travelPreference", option)
                        }
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="form-group seeking-desc-group"
              style={{ marginTop: "20px" }}
            >
              <label>Descreva o que você está buscando</label>
              <textarea
                name="seekingDescription"
                value={formData.seekingDescription || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({ ...prev, seekingDescription: val }));
                }}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, seekingDescription: true }))
                }
                maxLength={3000}
                placeholder="Conte mais sobre o que você espera encontrar no Mimo Cupido"
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "4px",
                  border:
                    touched.seekingDescription && errors.seekingDescription
                      ? "1px solid #ff0000"
                      : "1px solid #ddd",
                  minHeight: "100px",
                  resize: "vertical",
                }}
              />
              {touched.seekingDescription && errors.seekingDescription && (
                <span className="error-message">
                  {errors.seekingDescription}
                </span>
              )}
              <div
                className="char-limit-info"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  color: "#999",
                  marginTop: "4px",
                }}
              >
                <span>
                  Não é permitido conteúdo sexualmente explícito ou ilícito
                  (como prostituição)
                </span>
                <div className="char-counter">
                  {formData.seekingDescription?.length || 0}/3000
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="steps-btn-secondary"
                onClick={handleBack}
                style={{
                  marginTop: "20px",
                }}
              >
                Voltar
              </button>
              <button
                className="steps-btn-primary"
                onClick={handleNext}
                style={{
                  marginTop: "20px",
                }}
                disabled={isLoading}
              >
                {isLoading ? "Criando conta..." : "Concluir cadastro"}
              </button>
            </div>

            <SuportLink />
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterStepsModal;
